<script setup lang="ts">
import {
    ref,
    computed,
    watch,
    onMounted,
    onUnmounted,
    nextTick,
    defineAsyncComponent,
    type PropType,
} from 'vue'
import { useI18n } from '~/composables/useI18n'
import { useUserStore } from '~/stores/user'
import { useAppStore } from '~/stores/app'
import { useChatDialogueStore } from '~/stores/chatDialogue'
import { useRoute } from '#imports'
import { emitter } from '~/utils/emitter'

const userStore = useUserStore()
const appStore = useAppStore()
const chatDialogueStore = useChatDialogueStore()
const route = useRoute()
const isLoggedIn = computed(() => userStore.isLoggedIn)
// 历史列表与工作台体量较大，默认灵感页无需同步解析它们。
// 分包后保留交互能力，同时缩短工具页首屏关键路径。
const LazyBatchOutputSide = defineAsyncComponent(
    () => import('./BatchOutputSide.vue')
)
const LazyPcWorkbench = defineAsyncComponent(
    () => import('./PcWorkbench.vue')
)
const isWorkbenchReady = ref(false)
// viewport、localStorage 只在客户端 hydration 后参与首屏分支。
const isClientHydrated = ref(false)
let workbenchIdleCallbackId: number | null = null
let workbenchMountTimer: number | null = null

const mountWorkbench = () => {
    isWorkbenchReady.value = true
    nextTick(() => {
        observeWorkbenchWrapper()
        // pcWorkbenchRef 在异步 chunk 加载完成后才就绪，nextTick 不够用；
        // 用 watch once 等 ref 首次填充后再做展开。
    })
}
// 用 Cookie 的 token 来判断登录态：SSR/CSR 都能读到，避免“服务端默认未登录导致灵感 tab 先被渲染为 active”的水合错乱
const cookieToken = useCookie<string | null>('token')

const hasAuthToken = () => {
    // hydration 前只使用 SSR/CSR 都能读取的 cookie，挂载后再补 localStorage。
    if (
        isClientHydrated.value &&
        import.meta.client &&
        typeof window !== 'undefined'
    ) {
        return !!(window.localStorage.getItem('token') || cookieToken.value)
    }
    return !!cookieToken.value
}

/**
 * 默认筛选条件下（all time + all category）的“是否有历史记录”
 * - null: 尚未从 BatchOutputSide 得到结论
 * - true/false: 已确定
 */
const hasAnyHistory = ref<boolean | null>(null)
// 用户是否手动切换过 tab（手动后不再强制自动切换）
const isTabManuallyChanged = ref(false)
// 工具页切换时，允许覆盖一次手动选择，确保默认落到正确 tab
const forceAutoSelectOnce = ref(false)

/**
 * 解决“切换侧边栏时从历史跳到灵感”的闪跳问题：
 * - 右侧首次渲染不要先显示 history 再切换
 * - 使用本地缓存记住“该账号是否有历史”，下次进入直接渲染正确 tab
 */
const getHistoryCacheKey = () => {
    // 优先用 uid；否则退化为 token 是否存在（不存 token 本身，避免敏感信息落盘）
    // 注意：userStore.token 可能在 store 恢复前为空，但 cookie/localStorage 里 token 已经存在，
    // 所以这里必须用 hasAuthToken() 来判断“是否有登录凭证”，否则会误用 guest key。
    // 另外：刷新/水合时 store 的 userInfo 可能还没恢复，但 localStorage 里通常已有 userInfo 缓存，
    // 这里优先从本地缓存兜底拿 uid，保证能命中 `uid:*` 的 has-history 缓存，避免刷新后默认落到“灵感”。
    const uidFromStorage = (() => {
        if (
            !isClientHydrated.value ||
            !import.meta.client ||
            typeof window === 'undefined'
        ) return null
        try {
            const raw = window.localStorage.getItem('userInfo')
            if (!raw) return null
            const parsed = JSON.parse(raw)
            const uid = parsed?.uid
            // 后端/历史数据可能把 uid 存成字符串，这里做宽容解析
            if (typeof uid === 'number' && Number.isFinite(uid)) return uid
            if (typeof uid === 'string') {
                const n = Number(uid)
                return Number.isFinite(n) ? n : null
            }
            return null
        } catch {
            return null
        }
    })()

    const uid = userStore.userId || uidFromStorage
    if (uid) return `weryai:right-output:has-history:uid:${uid}`
    if (hasAuthToken()) return `weryai:right-output:has-history:token-present`
    return `weryai:right-output:has-history:guest`
}

// key 会随着 uid 恢复（例如从 undefined -> 20390）而变化；需要监听变化并重新读取缓存，否则会出现“缓存=1但tab仍是灵感”
const historyCacheKey = computed(() => getHistoryCacheKey())

const readCachedHasHistory = (): boolean | null => {
    if (
        !isClientHydrated.value ||
        !import.meta.client ||
        typeof window === 'undefined'
    ) return null
    try {
        const v = window.localStorage.getItem(historyCacheKey.value)
        if (v === '1') return true
        if (v === '0') return false
        return null
    } catch {
        return null
    }
}

const writeCachedHasHistory = (val: boolean) => {
    if (!import.meta.client || typeof window === 'undefined') return
    try {
        window.localStorage.setItem(historyCacheKey.value, val ? '1' : '0')
    } catch {
        // ignore
    }
}

// 灵感组件分类
const props = defineProps({
    inspirationType: {
        type: String,
        default: '',
    },
    /**
     * /history 专用：当 inspirationType 为空字符串时仍展示“灵感”Tab（后端约定 type='' 返回全量）
     * 其它页面默认不打开，避免漏传 inspirationType 时意外出现“灵感”Tab。
     */
    showAllInspiration: {
        type: Boolean,
        default: false,
    },
    // 传给 InspirationWaterFull 的 types（可选）
    types: {
        type: Array as () => string[],
        default: () => [],
    },
    // 静态demo推荐列表
    demo_list: {
        type: Array as () => any[],
        default: () => [],
    },
    // 工具演示banner
    tool_banner: {
        type: Object as () => {
            img: string
            video: string
        },
        default: () => ({
            img: '',
            video: '',
        }),
    },
    panelHandoff: {
        type: Object as PropType<{
            tab: 'history' | 'inspiration' | 'chat'
            category: 'all' | 'IMAGE' | 'VIDEO' | 'TOOL' | 'MUSIC'
            ts: number
        } | null>,
        default: null,
    },
})

if (props.panelHandoff?.tab) {
    isTabManuallyChanged.value = true
}

const { t } = useI18n()

// 检测是否是移动端（< 1024px，与 tool 布局保持一致）
const isMobile = ref(false)
const checkMobile = () => {
    isMobile.value = window.innerWidth < 1024
}

// 判断是否强制渲染（在 history 页面始终渲染）
const isForceRender = computed(() => {
    return route.path.includes('/history')
})

// 控制组件是否可见的最终判断
const shouldRender = computed(() => {
    // 如果是 PC 端，或者是在 history 页面，则渲染
    return !isMobile.value || isForceRender.value
})

// 添加 header 的 ref
const headerRef = ref<HTMLElement | null>(null)

// header 是否在可视区域
const isHeaderVisible = ref(true)

// 通过 provide 将状态传递给子组件
provide('isHeaderVisible', isHeaderVisible)

// Intersection Observer 用于监听 header 的可见性
let intersectionObserver: IntersectionObserver | null = null

onMounted(() => {
    checkMobile()
    window.addEventListener('resize', checkMobile)

    // 使用 nextTick 确保 DOM 已渲染后再设置 Intersection Observer
    nextTick(() => {
        // 设置 Intersection Observer 监听 headerRef 的可见性
        if (headerRef.value && window.IntersectionObserver) {
            intersectionObserver = new IntersectionObserver(
                entries => {
                    entries.forEach(entry => {
                        // 当 header 进入或离开可视区域时更新状态
                        isHeaderVisible.value = entry.isIntersecting
                    })
                },
                {
                    // 设置根边距，可以提前触发（可选）
                    rootMargin: '0px',
                    // 当元素完全可见或部分可见时触发
                    threshold: 0,
                }
            )
            intersectionObserver.observe(headerRef.value as unknown as Element)
        }
    })
})

// 生成提交后强制切到历史 tab（覆盖所有 tool 页面）
const handleCreationSubmitted = () => {
    currentTab.value = 'history'
}
emitter.on('refresh-creation-history', handleCreationSubmitted)

/** 支付结果「去创作」等：不刷新列表，仅与缓存一致地落在历史 Tab */
const handlePreferRightOutputHistoryTab = () => {
    hasAnyHistory.value = true
    writeCachedHasHistory(true)
    currentTab.value = 'history'
}
emitter.on('prefer-right-output-history-tab', handlePreferRightOutputHistoryTab)

onUnmounted(() => {
    window.removeEventListener('resize', checkMobile)
    emitter.off('refresh-creation-history', handleCreationSubmitted)
    emitter.off(
        'prefer-right-output-history-tab',
        handlePreferRightOutputHistoryTab
    )

    // 清理 Intersection Observer
    if (intersectionObserver) {
        intersectionObserver.disconnect()
        intersectionObserver = null
    }
    if (workbenchResizeObserver) {
        workbenchResizeObserver.disconnect()
        workbenchResizeObserver = null
    }
    if (workbenchIdleCallbackId !== null) {
        const idleWindow = window as Window & {
            cancelIdleCallback?: (id: number) => void
        }
        idleWindow.cancelIdleCallback?.(workbenchIdleCallbackId)
        workbenchIdleCallbackId = null
    }
    if (workbenchMountTimer !== null) {
        window.clearTimeout(workbenchMountTimer)
        workbenchMountTimer = null
    }

    // 禁用状态仅当前页面生效：离开工具页时重置为全开启
    chatDialogueStore.resetAllDialoguesEnabled()

    // 离开时重置 activeTab 状态，避免其他 layout 残留 'chat' 类型
    appStore.setRightOutputActiveTab('history')
})

// 下拉框触发方式：移动端点击，桌面端悬停
const dropdownTrigger = computed(() => (isMobile.value ? 'click' : 'hover'))

// 判断是否显示灵感按钮（提前定义，供 getInitialTab 使用）
const showInspirationTab = computed(() => {
    // 移动端不显示灵感广场 tab（仅在 /history 页面生效）
    if (isMobile.value && props.showAllInspiration) return false

    // /history 专用：允许空 inspirationType 显示全量灵感
    if (props.showAllInspiration && !props.inspirationType) return true
    const allowedTypes = [
        'TEMPLATE_CREATION',
        'TEXT_TO_IMG',
        'IMG_TO_IMG',
        'TEXT_TO_VIDEO',
        'IMG_TO_VIDEO',
        'IMAGE_EFFECT_TEMPLATE_CREATION',
        'VIDEO_EFFECT_TEMPLATE_CREATION',
        'MUSIC_CREATION_GENERATION',
        'Video_Face_Change'
    ]
    return allowedTypes.includes(props.inspirationType)
})

// tab栏切换
// 初始化时根据缓存和登录状态决定默认tab，避免刷新后先显示灵感再切换的闪跳问题
const getInitialTab = (): 'history' | 'inspiration' | 'chat' => {
    if (props.panelHandoff?.tab) {
        return props.panelHandoff.tab
    }
    // 已登录：检查缓存的历史记录状态
    if (hasAuthToken()) {
        const cachedHasHistory = readCachedHasHistory()
        if (cachedHasHistory === true) return 'history'
        if (cachedHasHistory === false)
            return showInspirationTab.value ? 'inspiration' : 'history'
        // 缓存为 null（首次访问或缓存被清除）
        // - 更符合直觉：当“当前没有历史记录”时不要默认选中历史
        // - 有历史的老用户通常会命中缓存直接选中历史；若缓存丢失，后续由 BatchOutputSide 拉取结果再自动切回历史
        return showInspirationTab.value ? 'inspiration' : 'history'
    }
    // 未登录：默认显示灵感（如果支持）
    return showInspirationTab.value ? 'inspiration' : 'history'
}

const currentTab = ref<'history' | 'inspiration' | 'chat'>(getInitialTab())
const historyPanelLoaded = ref(currentTab.value === 'history')

// 是否显示 Chat Tab（/history 页面不显示，移动端不显示）
const showChatTab = computed(() => !props.showAllInspiration && !isMobile.value)

// 同步 activeTab 到 appStore（单一数据源：tool.vue 和 PcWorkbench 都从 appStore 读取）
watch(currentTab, (tab) => {
    if (tab === 'history') {
        historyPanelLoaded.value = true
    }
    appStore.setRightOutputActiveTab(tab)
}, { immediate: true })

const activeTabStyle = { backgroundColor: 'rgba(38, 38, 42, 1)', color: '#fff' } as const
const changeTab = (e: MouseEvent) => {
    isTabManuallyChanged.value = true
    const target = e.currentTarget as HTMLElement
    const type = target.dataset.type
    if (type === 'history' || type === 'inspiration' || type === 'chat') {
        currentTab.value = type
    }
}

const applyPanelHandoff = (
    handoff: {
        tab: 'history' | 'inspiration' | 'chat'
        category: 'all' | 'IMAGE' | 'VIDEO' | 'TOOL' | 'MUSIC'
        ts: number
    } | null
) => {
    if (!handoff) return
    isTabManuallyChanged.value = true
    currentTab.value = handoff.tab
    query.value.category = handoff.category
    currentQueryObj.value.category = handoff.category
}

// 历史记录查询数据
const query = ref({
    time: 'all',
    category: props.panelHandoff?.category || 'all',
    startTime: '',
    endTime: '',
})

// 传递给子组件的数据
const currentQueryObj = ref({
    time: 'all',
    category: props.panelHandoff?.category || 'all',
})

watch(
    () => props.panelHandoff,
    handoff => {
        applyPanelHandoff(handoff)
    }
)

const timeEnums = computed(() => {
    return [
        {
            label: t('batchTools.allTime'),
            value: 'all',
        },
        {
            label: t('batchTools.threeDays'),
            value: 'threeDays',
        },
        {
            label: t('batchTools.onWeek'),
            value: 'onWeek',
        },
        {
            label: t('batchTools.oneMonth'),
            value: 'oneMonth',
        },
    ]
})

const generate_type_list = computed(() => {
    return [
        {
            label: t('batchTools.allType'),
            value: 'all' as const,
        },
        {
            label: t('batchTools.IMAGE'),
            value: 'IMAGE' as const,
        },
        {
            label: t('batchTools.VIDEO'),
            value: 'VIDEO' as const,
        },
        {
            label: t('batchTools.AUDIO'),
            value: 'MUSIC' as const,
        },
    ]
})

/**
 * 业务规则：
 * - 未登录 → 展示灵感（如果该工具支持灵感）
 * - 已登录且有创作记录 → 展示历史
 * - 已登录但无创作记录 → 展示灵感（如果该工具支持灵感）
 */
const canShowInspiration = computed(() => showInspirationTab.value)
// 需求变更：即使无历史也保留“历史”Tab（但无历史时默认展示“灵感”）
const canShowHistoryTab = computed(() => !isLoggedIn.value)

const autoSelectTabIfNeeded = () => {
    if (isTabManuallyChanged.value && !forceAutoSelectOnce.value) return
    // 用 token 是否存在判断登录态（SSR/CSR 一致），避免仅靠 store 的异步恢复造成闪跳
    const actuallyLoggedIn = hasAuthToken()

    // 未登录：如果支持灵感就切灵感，否则退回历史（兼容无灵感的工具）
    if (!actuallyLoggedIn) {
        currentTab.value = canShowInspiration.value ? 'inspiration' : 'history'
        return
    }

    // 已登录：若已确认无历史，则优先灵感；有历史则历史
    if (hasAnyHistory.value === false) {
        currentTab.value = canShowInspiration.value ? 'inspiration' : 'history'
        return
    }
    if (hasAnyHistory.value === true) {
        currentTab.value = 'history'
    }
    if (forceAutoSelectOnce.value) {
        forceAutoSelectOnce.value = false
    }
}

const handleHistoryStatus = (payload: {
    hasHistory: boolean
    isDefaultQuery: boolean
}) => {
    // 只用默认筛选条件来判定“是否有历史记录”
    if (payload.isDefaultQuery) {
        hasAnyHistory.value = payload.hasHistory
        writeCachedHasHistory(payload.hasHistory)
        autoSelectTabIfNeeded()
    }
}

// 在 setup 阶段尽早读缓存，减少首次渲染闪跳（仅客户端生效）
const cachedHasHistory = readCachedHasHistory()
if (cachedHasHistory !== null) {
    hasAnyHistory.value = cachedHasHistory
}
// 先按当前（可能来自缓存的）状态选择一次 tab
autoSelectTabIfNeeded()

// 当缓存 key 发生变化时（最常见是 uid 恢复出来），重新读取缓存并触发一次自动选 tab
watch(
    () => historyCacheKey.value,
    () => {
        const v = readCachedHasHistory()
        if (v !== null) {
            hasAnyHistory.value = v
        }
        autoSelectTabIfNeeded()
    }
)

// 关键兜底：刷新时服务端渲染可能先把 tab 渲染成“灵感”，客户端水合后再做一次强制校准
// 只要本地缓存里 has-history=1，最终一定落到“历史”
onMounted(() => {
    if (!import.meta.client) return
    isClientHydrated.value = true
    const v = readCachedHasHistory()
    if (v !== null) {
        hasAnyHistory.value = v
    }
    autoSelectTabIfNeeded()

    // 初始化时，检查是否需要展开（不限制 tab 类型）
    nextTick(() => {
        expandWorkbenchIfCollapsed()
    })
})

onMounted(() => {
    nextTick(() => {
        observeWorkbenchWrapper()
    })
})

// 工作台不参与 SEO 首屏内容，交给浏览器空闲时间加载；超时保证低端设备仍能及时使用。
onMounted(() => {
    // 视频增强页的工作台位于首屏可视区；延迟到空闲阶段才开始加载会把首次绘制推迟为 LCP。
    if (route.path.includes('/video-upscaler')) {
        mountWorkbench()
        return
    }

    const idleWindow = window as Window & {
        requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
    }
    if (idleWindow.requestIdleCallback) {
        workbenchIdleCallbackId = idleWindow.requestIdleCallback(mountWorkbench, {
            timeout: 1200,
        })
        return
    }
    workbenchMountTimer = window.setTimeout(mountWorkbench, 0)
})

watch([isMobile, currentTab, showChatTab], () => {
    nextTick(() => {
        observeWorkbenchWrapper()
    })
})

watch(
    () => isLoggedIn.value,
    newVal => {
        // 登录态变化时，重新走一遍自动选 tab（但不打断用户手动选择）
        // 未登录时 hasAnyHistory 的意义不大，但保持原值不会影响 canShowHistoryTab
        autoSelectTabIfNeeded()
    },
    {
        immediate: true,
    }
)

// 工具页切换时重置“手动选择”标记，并强制走一次自动选 tab
watch(
    () => route.path,
    () => {
        forceAutoSelectOnce.value = true
        isTabManuallyChanged.value = false
        const v = readCachedHasHistory()
        if (v !== null) {
            hasAnyHistory.value = v
        }
        autoSelectTabIfNeeded()
    }
)

const is_recommend_demo = computed(() => ['Video_Face_Change'].includes(props.inspirationType))

const chatPanelRef = ref<any>(null)
const pcWorkbenchRef = ref<any>(null)

// 异步 chunk 加载完成后 ref 才就绪，用 watch once 补上初始化展开逻辑
watch(pcWorkbenchRef, (ref) => {
    if (ref) expandWorkbenchIfCollapsed()
}, { once: true })
const inspirationPanelRef = ref<HTMLElement | null>(null)
const workbenchWrapperRef = ref<HTMLElement | null>(null)
const workbenchCompact = ref<boolean | null>(null)
const workbenchBottomInset = ref(0)
const historyBottomExpanded = ref(false)
const historyBottomExpandAnchorScrollTop = ref<number | null>(null)
const suppressHistoryBottomAutoExpand = ref(false)
const suppressInspirationTopAutoExpandUntilTs = ref(0)
const lastInspirationScrollTop = ref(0)
const FLOATING_WORKBENCH_BOTTOM_OFFSET = 20
const FLOATING_WORKBENCH_SAFE_GAP = 16
let workbenchResizeObserver: ResizeObserver | null = null

const HISTORY_BOTTOM_EXPAND_THRESHOLD = 10
const HISTORY_BOTTOM_RELEASE_THRESHOLD = 80
const HISTORY_BOTTOM_COLLAPSE_SCROLL_DELTA = 48
const INSPIRATION_TOP_EXPAND_THRESHOLD = 20
const INSPIRATION_TOP_AUTO_EXPAND_SUPPRESS_MS = 260

// 追踪 PcWorkbench 当前一级类别，用于 tab 切换时联动输入框模式
const workbenchMainCategorySelected = ref<string | null>(null)

// 根据当前 tab 动态计算 PcWorkbench 的一级类别（与 chat/[[id]].vue 的 workbenchPrimaryTab 逻辑一致）
const workbenchPrimaryTab = computed(() => {
    if (currentTab.value === 'chat') return 'CHAT'
    // history / inspiration：若工作台仍在 CHAT 模式则切回创作模式
    if (workbenchMainCategorySelected.value === 'CHAT') {
        return 'IMAGE_GENERATE'
    }
    return undefined
})

const updateWorkbenchBottomInset = () => {
    if (isMobile.value || (showChatTab.value && currentTab.value === 'chat')) {
        workbenchBottomInset.value = 0
        return
    }

    const wrapper = workbenchWrapperRef.value
    if (!wrapper) {
        workbenchBottomInset.value = 0
        return
    }

    const nextHeight = Math.ceil(wrapper.getBoundingClientRect().height)
    workbenchBottomInset.value = nextHeight > 0
        ? nextHeight + FLOATING_WORKBENCH_BOTTOM_OFFSET + FLOATING_WORKBENCH_SAFE_GAP
        : 0
}

const observeWorkbenchWrapper = () => {
    workbenchResizeObserver?.disconnect()
    workbenchResizeObserver = null

    if (!import.meta.client || !window.ResizeObserver || !workbenchWrapperRef.value) {
        updateWorkbenchBottomInset()
        return
    }

    workbenchResizeObserver = new ResizeObserver(() => {
        updateWorkbenchBottomInset()
    })
    workbenchResizeObserver.observe(workbenchWrapperRef.value as unknown as Element)
    updateWorkbenchBottomInset()
}

// 展开工作台（仅在左侧面板收起时，不破坏互斥规则）
const expandWorkbenchIfCollapsed = () => {
    if (appStore.creatorPanelCollapsed) {
        pcWorkbenchRef.value?.expand?.()
    }
}

const collapseWorkbenchIfExpanded = () => {
    if (appStore.creatorPanelCollapsed) {
        pcWorkbenchRef.value?.collapse?.()
    }
}

// 监听 Tab 切换：
// - history: 保持原有默认展开
// - inspiration: 仅当当前位于顶部时默认展开，否则保持折叠
watch(
    () => currentTab.value,
    (newTab) => {
        historyBottomExpanded.value = false
        historyBottomExpandAnchorScrollTop.value = null
        suppressHistoryBottomAutoExpand.value = false
        suppressInspirationTopAutoExpandUntilTs.value = 0
        nextTick(() => {
            if (newTab === 'inspiration') {
                if (lastInspirationScrollTop.value <= INSPIRATION_TOP_EXPAND_THRESHOLD) {
                    expandWorkbenchIfCollapsed()
                } else {
                    collapseWorkbenchIfExpanded()
                }
                return
            }
            expandWorkbenchIfCollapsed()
        })
    }
)

const handleInspirationScroll = (payload: { scrollTop: number; distanceFromBottom: number }) => {
    if (!appStore.creatorPanelCollapsed) return
    lastInspirationScrollTop.value = payload.scrollTop

    if (
        Date.now() < suppressInspirationTopAutoExpandUntilTs.value &&
        payload.scrollTop <= INSPIRATION_TOP_EXPAND_THRESHOLD
    ) {
        return
    }

    if (payload.scrollTop <= INSPIRATION_TOP_EXPAND_THRESHOLD) {
        pcWorkbenchRef.value?.expand?.()
        return
    }

    if (payload.scrollTop > 20) {
        collapseWorkbenchIfExpanded()
    }
}

const handleHistoryScrollState = (payload: { scrollTop: number; distanceFromBottom: number }) => {
    if (!appStore.creatorPanelCollapsed) return

    if (suppressHistoryBottomAutoExpand.value) {
        if (payload.distanceFromBottom <= HISTORY_BOTTOM_RELEASE_THRESHOLD) {
            return
        }
        suppressHistoryBottomAutoExpand.value = false
        historyBottomExpanded.value = false
        historyBottomExpandAnchorScrollTop.value = null
    }

    if (payload.distanceFromBottom <= HISTORY_BOTTOM_EXPAND_THRESHOLD) {
        historyBottomExpanded.value = true
        historyBottomExpandAnchorScrollTop.value = payload.scrollTop
        pcWorkbenchRef.value?.expand?.()
        return
    }

    if (historyBottomExpanded.value) {
        const anchorScrollTop = historyBottomExpandAnchorScrollTop.value ?? payload.scrollTop
        const hasUserScrolledUpEnough =
            payload.scrollTop <= anchorScrollTop - HISTORY_BOTTOM_COLLAPSE_SCROLL_DELTA

        if (!hasUserScrolledUpEnough) {
            pcWorkbenchRef.value?.expand?.()
            return
        }

        historyBottomExpanded.value = false
        historyBottomExpandAnchorScrollTop.value = null
    }

    if (payload.scrollTop > 20) {
        collapseWorkbenchIfExpanded()
    }
}

const handleWorkbenchCompactChange = (compact: boolean) => {
    workbenchCompact.value = compact
    if (!compact) return

    // Respect manual outside-click compacting until the user leaves the
    // corresponding auto-expand zone.
    if (currentTab.value === 'history') {
        historyBottomExpanded.value = false
        historyBottomExpandAnchorScrollTop.value = null
        suppressHistoryBottomAutoExpand.value = true
    } else if (currentTab.value === 'inspiration') {
        suppressInspirationTopAutoExpandUntilTs.value =
            Date.now() + INSPIRATION_TOP_AUTO_EXPAND_SUPPRESS_MS
    }
}

// PcWorkbench 的 send 事件 → 转发给 ChatPanel（仅 Chat Tab 激活时）
const handleWorkbenchSend = (payload: { text: string; modelKey: string; done?: (ok: boolean) => void }) => {
    if (appStore.rightOutputActiveTab === 'chat') {
        chatPanelRef.value?.handleSend(payload)
    }
}

// PcWorkbench 切换到 CHAT 模式时自动激活 Chat Tab
const handleSwitchToChat = () => {
    if (showChatTab.value) {
        currentTab.value = 'chat'
        isTabManuallyChanged.value = true
    }
}

// PcWorkbench 从 CHAT 切到创作类别时，自动切回 history tab
const handleSwitchToCreation = () => {
    if (currentTab.value === 'chat') {
        currentTab.value = 'history'
        isTabManuallyChanged.value = true
    }
}

// PcWorkbench 一级/二级类别变化 → 更新本地追踪状态
const handleWorkbenchSelectionsChange = (payload: {
    mainCategorySelected: string | null
    specificCategorySelected: string | null
}) => {
    workbenchMainCategorySelected.value = payload.mainCategorySelected
}

// PcWorkbench 窗口数量变化 → 转发给 ChatPanel
const handleDialogueCountUpdate = (count: number) => {
    if (appStore.rightOutputActiveTab === 'chat') {
        chatPanelRef.value?.handleSetDialogueCount(count)
    }
}
</script>

<template>
    <div class="output-plate flex-1 min-h-0 min-w-0 relative w-full">
        <!-- 移动端 /history 页面显示项目选择器 -->
        <div v-if="isMobile && showAllInspiration" class="mobile-history-header">
            <HistoryProjectSelector />
        </div>

        <!-- 与 image_tab_standalone 一致：顶栏悬浮 + 下方滚动区顶内边距（所有工具页 RightOutputPlate 共用） -->
        <div class="output-plate__canvas">
            <header ref="headerRef" v-if="!isMobile"
                class="output-plate__top-bar output-plate__top-bar--floating" :class="{
                    'output-plate__top-bar--inspiration-only': currentTab === 'inspiration',
                    /* 聊天侧灵感列表默认全宽，与左侧创作面板收起/展开无关 */
                    'output-plate__top-bar--inspiration-full': currentTab === 'inspiration',
                }">
                <!-- 右侧 tab 强依赖 localStorage/cookie/store（仅客户端可靠），为避免 SSR/水合不一致导致的高亮错乱，这里改为纯客户端渲染 -->
                <ClientOnly>
                    <div class="tabPlate output-plate__tab-pill" role="tablist" aria-label="Output tabs">
                        <button type="button" role="tab" id="tab-history" class="option"
                            :class="{ active: currentTab === 'history' }" :style="(!isMobile && currentTab === 'history') ? activeTabStyle : null
                                " aria-label="history tab" :aria-selected="currentTab === 'history'"
                            :aria-controls="currentTab === 'history' ? 'tabpanel-history' : undefined" data-type="history"
                            @click="changeTab">
                            {{ isMobile ? t('inspiration.tabHistoryMobile') : t('inspiration.tabHistory') }}
                        </button>
                        <button v-if="canShowInspiration" type="button" role="tab" id="tab-inspiration" class="option"
                            :class="{ active: currentTab === 'inspiration' }" :style="(!isMobile && currentTab === 'inspiration') ? activeTabStyle : null
                                " aria-label="inspiration tab" :aria-selected="currentTab === 'inspiration'"
                            :aria-controls="currentTab === 'inspiration' ? 'tabpanel-inspiration' : undefined"
                            data-type="inspiration" @click="changeTab">
                            {{ isMobile ? t('inspiration.tabInspirationMobile') : t('inspiration.tabInspiration') }}
                        </button>
                        <!-- <button v-if="showChatTab" type="button" role="tab" id="tab-chat" class="option"
                            :class="{ active: currentTab === 'chat' }" :style="(!isMobile && currentTab === 'chat') ? activeTabStyle : null
                                " aria-label="chat tab" :aria-selected="currentTab === 'chat'"
                            :aria-controls="currentTab === 'chat' ? 'tabpanel-chat' : undefined"
                            data-type="chat" @click="changeTab">
                            Chat
                        </button> -->
                    </div>
                </ClientOnly>
                <section>
                    <div ref="queryRef" v-if="currentTab === 'history' && !showAllInspiration"
                        class="output-plate__filter-chips text-sm gap-3 text-white flex items-center justify-center">
                        <ClientOnly>
                            <el-dropdown class="flex items-center">
                                <!-- Element Plus 会给 trigger 注入 el-tooltip__trigger 并设置默认灰色，强制白色保证对比度 -->
                                <span class="flex items-center text-sm gap-1 !text-white">
                                    {{
                                        timeEnums.find(
                                            item => item.value === query.time
                                        )?.label
                                    }}
                                    <span class="i-down-icon w-3 h-3"></span>
                                </span>
                                <template #dropdown>
                                    <el-dropdown-menu>
                                        <el-dropdown-item v-for="item in timeEnums" :key="item.value" @click="
                                            () => {
                                                currentQueryObj.time =
                                                    item.value
                                                query.time = item.value
                                            }
                                        " :class="{
                                            '!text-text-active':
                                                query.time === item.value,
                                        }">{{ item.label }}</el-dropdown-item>
                                    </el-dropdown-menu>
                                </template>
                            </el-dropdown>
                        </ClientOnly>

                        <div class="w-[1px] h-[13px] bg-[#4C4F5B]"></div>
                        <ClientOnly>
                            <el-dropdown class="flex items-center">
                                <!-- 同上：强制 trigger 文字为白色，避免 Lighthouse 报对比度不足 -->
                                <span class="flex items-center text-sm gap-1 !text-white">
                                    {{
                                        generate_type_list.find(
                                            item => item.value === query.category
                                        )?.label
                                    }}
                                    <span class="i-down-icon w-3 h-3"></span>
                                </span>
                                <template #dropdown>
                                    <el-dropdown-menu>
                                        <el-dropdown-item v-for="item in generate_type_list" :key="item.value" @click="
                                            () => {
                                                currentQueryObj.category =
                                                    item.value
                                                query.category = item.value
                                            }
                                        " :class="{
                                            '!text-text-active':
                                                query.category === item.value,
                                        }">{{ item.label }}</el-dropdown-item>
                                    </el-dropdown-menu>
                                </template>
                            </el-dropdown>
                        </ClientOnly>
                    </div>
                </section>
            </header>

            <div class="output-plate__scroll-stage">
                <div v-if="historyPanelLoaded" v-show="currentTab === 'history'"
                    class="output-plate__content-shell output-plate__content-shell--history">
                    <ClientOnly>
                        <main v-if="!isMobile || showAllInspiration">
                            <div id="tabpanel-history" role="tabpanel" aria-labelledby="tab-history"
                                :aria-hidden="currentTab !== 'history'">
                                <LazyBatchOutputSide :currentQueryObj="currentQueryObj" origin="project_detail"
                                    :workbench-compact="workbenchCompact"
                                    :workbench-bottom-inset="workbenchBottomInset"
                                    @history-status="handleHistoryStatus"
                                    @scroll-state="handleHistoryScrollState"></LazyBatchOutputSide>
                            </div>
                        </main>
                        <template #fallback>
                            <div class="output-plate-placeholder"></div>
                        </template>
                    </ClientOnly>
                </div>

                <div v-if="canShowInspiration" v-show="currentTab === 'inspiration'"
                    class="output-plate__inspiration-host">
                    <ClientOnly>
                        <div ref="inspirationPanelRef" id="tabpanel-inspiration" role="tabpanel"
                            aria-labelledby="tab-inspiration" :aria-hidden="currentTab !== 'inspiration'">
                            <ui-recommend-demo v-if="is_recommend_demo" :demo_list="demo_list"
                                :tool_banner="tool_banner"></ui-recommend-demo>
                            <inspiration-water-full v-else showType="inspiration"
                                :inspirationType="inspirationType" uiVariant="tool-page-pc"
                                :workbench-bottom-inset="workbenchBottomInset" :waterfall-config="{ gap: 2 }"
                                @scroll-state="handleInspirationScroll"></inspiration-water-full>
                        </div>
                    </ClientOnly>
                </div>
            </div>
        </div>
        <div v-if="!isMobile" ref="workbenchWrapperRef" class="right-output-workbench-wrapper"
            :class="{ 'in-flow': showChatTab && currentTab === 'chat' }">
            <LazyPcWorkbench v-if="isWorkbenchReady" ref="pcWorkbenchRef"
                :default-primary-tab="workbenchPrimaryTab"
                :outside-click-collapse-context-tab="currentTab"
                :lock-expanded="currentTab === 'chat' && appStore.creatorPanelCollapsed"
                :dialogue-count="chatDialogueStore.dialogueCount"
                @compact-change="handleWorkbenchCompactChange"
                @send="handleWorkbenchSend"
                @switch-to-chat="handleSwitchToChat"
                @switch-to-creation="handleSwitchToCreation"
                @selections-change="handleWorkbenchSelectionsChange"
                @update:dialogue-count="handleDialogueCountUpdate" />
            <div v-else class="right-output-workbench-placeholder" aria-hidden="true"></div>
        </div>
    </div>
</template>

<style scoped lang="less">
.right-output-workbench-wrapper {
    position: fixed;
    bottom: 20px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: stretch;
    padding: 0 20%;
    z-index: 100;
    pointer-events: none;

    >* {
        width: 100%;
    }

    &.in-flow {
        position: relative;
        bottom: auto;
        left: auto;
        right: auto;
        flex-shrink: 0;
        display: flex;
        justify-content: center;
        padding: 20px 20% 24px;
        z-index: 98;
        overflow: visible;
        pointer-events: auto;
    }
}
.right-output-workbench-wrapper :deep(.workbench-container) {
    pointer-events: auto;
}

.right-output-workbench-placeholder {
    width: 100%;
    min-height: 74px;
}

.output-plate {
    display: flex;
    flex-direction: column;
    min-width: 0;
    width: 100%;
    max-width: 100%;
    background-color: #060608;
    border-radius: 12px;
    padding: 16px;

    @media (min-width: 1024px) {
        padding: 16px 20px 20px;
    }
}

/* 右侧主画布：顶栏 absolute + 滚动区占满剩余高度（对齐 image_tab_standalone） */
.output-plate__canvas {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    position: relative;
    width: 100%;
    max-width: 100%;
}

.output-plate__scroll-stage {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    overscroll-behavior: contain;
}

/* 桌面：顶栏浮在滚动内容之上，scroll-stage 用 padding-top 让出顶栏高度 */
@media (min-width: 1024px) {
    .output-plate__top-bar--floating {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        z-index: 40;
        margin: 0;
        pointer-events: none;

        > * {
            pointer-events: auto;
        }
    }

    .output-plate__scroll-stage {
        /* 顶栏约 40px + 上下留白，与原型 pt-24 同目的 */
        padding-top: 55px;
    }
}

/* 仅历史列表：1120 居中；灵感 Tab 时本块 v-show 隐藏不占位 */
.output-plate__content-shell--history {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    width: 100%;
    max-width: 1120px;
    margin: 0 auto;
}

.output-plate__inspiration-host {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    width: 100%;
}

.output-plate-placeholder {
    min-height: 300px;
}

header {
    display: flex;
    height: 40px;
    -moz-column-gap: 10px;
    column-gap: 10px;
    margin: 10px 0;
    flex-shrink: 0;
    border-radius: 20px;
    padding: 2px;
    justify-content: space-between;

    .category {
        border: 1px solid rgba(207, 211, 220, 0.5);
        border-radius: 6px;
        padding: 0 10px;
        height: 30px;
    }

    .option {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 34px;
        padding: 0 16px;
        font-size: 14px;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.5);
        background-color: transparent;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;

        &:focus-visible {
            outline: 2px solid #99f768;
            outline-offset: 2px;
        }

        &:hover {
            color: rgba(255, 255, 255, 0.7);
        }

        &.active {
            color: #fff;
            background-color: rgba(38, 38, 42, 1);
        }
    }
}

.tabPlate {
    display: flex;
    align-items: center;
    height: 40px;
    gap: 2px;
    padding: 2px;
    background-color: rgba(16, 16, 18, 1);
    border: 1px solid rgba(29, 29, 32, 1);
    border-radius: 8px;
    flex-shrink: 0;
    width: -moz-fit-content;
    width: fit-content;
}

/* 对齐 image_tab_standalone：顶栏玻璃态「历史 / 灵感」+ 右侧筛选胶囊 */
.output-plate__top-bar {
    @media (min-width: 1024px) {
        margin: 0 0 14px;
        padding: 4px 2px;
        align-items: center;
    }
}

.output-plate__top-bar--inspiration-only {
    @media (min-width: 1024px) {
        margin-bottom: 0;
    }
}

/* 灵感 + 左侧栏收起：Tab 与下方全宽列表左缘对齐 */
.output-plate__top-bar--inspiration-full {
    @media (min-width: 1024px) {
        // justify-content: flex-start;
    }
}

.output-plate__top-bar .tabPlate.output-plate__tab-pill {
    @media (min-width: 1024px) {
        background: rgba(255, 255, 255, 0.06);
        border: 1px solid rgba(255, 255, 255, 0.1);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
        -webkit-backdrop-filter: blur(12px);
        backdrop-filter: blur(12px);
        border-radius: 12px;
    }
}

.output-plate__filter-chips {
    min-height: 38px;
    padding: 8px 14px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
}

main {
    overflow: hidden;
    /* 由 BatchOutputSide 内部处理滚动，避免嵌套滚动条 */
    flex: 1;
    min-height: 0;
    position: relative;
    display: flex;
    flex-direction: column;
    overscroll-behavior: contain;
}

/* Tab 面板需要占满 main 的可用高度（否则 overflow-y 无法生效） */
#tabpanel-history,
#tabpanel-chat {
    flex: 1;
    min-height: 0;
}

/* 灵感在 scroll-stage 内全宽；瀑布流滚动在本容器（隐藏滚动条，避免拖动时带动整页滚动） */
#tabpanel-inspiration {
    flex: 1;
    min-height: 0;
    width: 100%;
    box-sizing: border-box;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
    }
}

/* 聊天/工具右侧灵感 Tab：桌面端始终占满输出区，不随左侧创作面板收起/展开变窄 */
@media (min-width: 1024px) {
    #tabpanel-inspiration {
        max-width: none;
    }
}

@media (max-width: 639px) {
    header {
        height: auto;
        flex-wrap: nowrap;
        gap: 8px;
        margin: 5px 0 12px;
    }

    .tabPlate {
        gap: 21px;
        padding: 0;
        background-color: transparent;
        border: none;
        border-radius: 0;
        height: auto;
        width: auto;
        flex: none;
        justify-content: flex-start;
    }

    .output-plate {
        padding: 0;
    }

    .option {
        position: relative;
        height: auto;
        padding: 0;
        padding-bottom: 7px;
        font-size: 16px;
        font-weight: 900;
        text-transform: uppercase;
        color: rgba(217, 217, 217, 0.5);
        background-color: transparent;
        border-radius: 0;

        &:hover {
            color: rgba(217, 217, 217, 0.7);
        }

        &.active {
            color: #fff;
            background-color: transparent;

            &::after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 3.5px;
                background-color: #99f768;
                border-radius: 9999px;
            }
        }
    }

    /* 让筛选器更紧凑 */
    header section:last-child>div {
        padding: 5px 10px !important;
        gap: 8px !important;
        font-size: 12px;
        height: 32px !important;
    }

    /* 筛选器文本更小 */
    header section:last-child .el-dropdown {
        font-size: 12px;
    }

    header section:last-child .i-down-icon {
        width: 10px !important;
        height: 10px !important;
    }

    .mobile-history-header {
        padding: 8px 0 12px;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
}
</style>
